import { useMemo } from 'react'
import type { LivelloPermesso, PermessoModulo } from '../api/types'
import { useAuth } from './AuthContext'

/**
 * I permessi per modulo di un ruolo personalizzato, dalla parte della console.
 *
 * Il catalogo dei moduli e il controllo vero stanno nel Control Plane
 * (app/services/permessi_service.py): le API di un modulo nascosto,
 * disattivato o in sola lettura rispondono 403 comunque. Qui si decide solo
 * cosa **mostrare** — il menu senza le voci nascoste, le voci disattivate in
 * grigio, la pagina bloccata o con l'avviso di sola lettura — perché un
 * pulsante che porta a un 403 è peggio di un pulsante che non c'è.
 */

/**
 * Il livello di una rotta della console, o `null` se la rotta non è di un
 * modulo (guida, presenza…) o l'utente non ha un ruolo personalizzato.
 *
 * Vince la rotta più lunga, come per la voce del menu evidenziata: con
 * `/flows` e `/flows/map` nello stesso elenco, `/flows/map/123` è della mappa.
 * La dashboard (`/`) vale solo per sé stessa.
 */
export function livelloRotta(
  permessi: PermessoModulo[] | null | undefined,
  pathname: string,
): LivelloPermesso | null {
  if (!permessi) return null
  let migliore: { lunghezza: number; livello: LivelloPermesso } | null = null
  for (const p of permessi) {
    for (const rotta of p.paths) {
      const corrisponde =
        rotta === '/' ? pathname === '/' : pathname === rotta || pathname.startsWith(rotta + '/')
      if (corrisponde && (migliore === null || rotta.length > migliore.lunghezza)) {
        migliore = { lunghezza: rotta.length, livello: p.level }
      }
    }
  }
  return migliore?.livello ?? null
}

/** La prima rotta consultabile: dove mandare chi atterra su un modulo chiuso. */
export function primaRottaAperta(permessi: PermessoModulo[] | null | undefined): string | null {
  if (!permessi) return '/'
  const aperto = permessi.find((p) => p.level === 'read' || p.level === 'write')
  return aperto?.paths[0] ?? null
}

/**
 * Il livello di un **modulo** per l'utente, per le funzioni che attraversano
 * più moduli: le sezioni della scheda di un CI (sicurezza, backup, IPAM,
 * routing, firewall, Wazuh…), i widget della dashboard, i distintivi della
 * barra in alto. Una sezione di un modulo nascosto o disattivato non si
 * mostra; di un modulo in lettura si mostra senza i comandi.
 *
 * Senza ruolo personalizzato vale il ruolo del prodotto: la sola lettura legge
 * soltanto, operatori e amministratori scrivono.
 */
export function livelloModulo(
  principal: { roles: string[]; permissions?: PermessoModulo[] | null } | null | undefined,
  modulo: string,
): LivelloPermesso {
  const livello = principal?.permissions?.find((p) => p.module === modulo)?.level ?? 'write'
  const scrivente = (principal?.roles ?? []).some((r) =>
    ['tenant_admin', 'tenant_operator', 'provider_admin'].includes(r),
  )
  return livello === 'write' && !scrivente ? 'read' : livello
}

/** «Posso vedere / posso scrivere questo modulo?», per i componenti. */
export function usePermessiModuli() {
  const { principal } = useAuth()
  // One object per principal, not one per render: the pages memoize their
  // «which widgets can this person see» on it, and a fresh object every
  // render made the wall displays rebuild their default dashboard (new widget
  // ids) at every tick of the clock — every widget remounted, every second.
  return useMemo(() => ({
    vede: (modulo: string) => {
      const l = livelloModulo(principal, modulo)
      return l === 'read' || l === 'write'
    },
    scrive: (modulo: string) => livelloModulo(principal, modulo) === 'write',
  }), [principal])
}
